import { Button } from "@progress/kendo-react-buttons";
import { PlusSquare } from "lucide-react";
import * as React from "react";
import { useEffect } from "react";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { useConditionBuilderContext } from "@/components/ConditionBuilder/context/ConditionBuilderContext";
import {
  ConditionEntry,
  ConditionLeaf,
} from "@/components/ConditionBuilder/types";
import {
  COMBINATOR_OPTIONS,
  createConditionGroup,
  createConditionLeaf,
  isConditionGroup,
} from "@/components/ConditionBuilder/utils/conditionTree";
import { FormDropDownList } from "@/components/Forms";
import AddGroupIcon from "@/icons/add_group.svg?react";
import ConditionRow from "./ConditionRow";

const PlusSVG = (props: React.ComponentProps<typeof PlusSquare>) => (
  <PlusSquare color="var(--tlx-status-info-icon)" {...props} />
);

interface ConditionGroupProps {
  /** RHF field name holding this group's array of conditions. */
  name: string;
  /** RHF field name holding this group's AND/OR combinator. */
  combinatorName: string;
  depth?: number;
  /**
   * Called by a nested group to remove itself from the parent when it
   * becomes empty. Passed down by the parent ConditionGroup.
   */
  onRemoveSelf?: () => void;
}

/** Returns true when a leaf is missing attribute, operator, or a non-empty value. */
const isLeafIncomplete = (entry: ConditionEntry): boolean => {
  if (isConditionGroup(entry)) return false;
  const { attribute, operator, value } = entry as ConditionLeaf;
  if (!attribute?.id || !operator?.id) return true;
  if (value === undefined || value === null || value === "") return true;
  if (Array.isArray(value) && value.length === 0) return true;
  return false;
};

const ConditionGroup: React.FC<ConditionGroupProps> = ({
  name,
  combinatorName,
  depth = 0,
  onRemoveSelf,
}) => {
  const methods = useFormContext();
  const { config } = useConditionBuilderContext();
  const { fields, append, remove, insert, update } = useFieldArray({
    control: methods.control,
    name,
    // ConditionEntry items have stable `id` fields generated by createConditionLeaf
    // / createConditionGroup. We use those instead of RHF's auto-generated ids.
    keyName: "_rhfKey",
  });

  // Render from useFieldArray.fields (NOT useWatch) for the row list.
  //
  // useFieldArray.remove(i) mutates the underlying form values array in place
  // (splice). useWatch's subscription receives a notification, but its
  // shallow-equality check sees the SAME array reference and skips the
  // re-render — so useWatch returns stale data after remove. The user's bug
  // was exactly this: form state shows 1 item after delete, but the UI keeps
  // showing 2. `fields` is RHF's separate React state on the useFieldArray
  // hook and IS updated on every append/insert/remove/move/swap/replace, so
  // rendering from it stays in sync with array mutations.
  //
  // We use useWatch separately below for `hasIncompleteRow`, which depends on
  // per-leaf VALUES (attribute/operator/value) that fields' snapshot does not
  // reflect when individual sub-paths update via setValue.
  const conditions = fields as unknown as ConditionEntry[];
  const watched = useWatch({ control: methods.control, name }) as
    | ConditionEntry[]
    | undefined;

  const showCombinator = fields.length > 1;
  const hasIncompleteRow = (watched ?? []).some(isLeafIncomplete);

  // Seeding policy: the parent form's defaultValues must already include one
  // empty leaf for this array (e.g. `serverRules: [createConditionLeaf()]` in
  // EMPTY_FORM_VALUES; per-tool seeding on transition to "custom" in
  // ToolAccessRulesStep). A previous post-mount seed via replace + reset was
  // brittle: it raced with useFieldArray's internal `fields` resync and caused
  // remove(i) to silently re-add the row. Keeping seeding in defaultValues
  // keeps useFieldArray.fields in sync from mount, so remove works on the
  // first click.

  // Remove this nested group from its parent when it becomes empty.
  useEffect(() => {
    if (depth > 0 && conditions.length === 0) {
      onRemoveSelf?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conditions.length]);

  // Auto-initialize combinator when first getting > 1 condition.
  useEffect(() => {
    if (!showCombinator) return;
    const current = methods.getValues(combinatorName as never) as
      | { id?: string }
      | string
      | undefined;
    if (typeof current === "object" && current?.id) return;
    if (typeof current === "string") {
      const option =
        COMBINATOR_OPTIONS.find(o => o.id === current) ?? COMBINATOR_OPTIONS[1];
      methods.setValue(combinatorName as never, option as never, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
      return;
    }
    methods.setValue(combinatorName as never, COMBINATOR_OPTIONS[1] as never, {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: false,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCombinator, combinatorName]);

  // Determine connector color type from the current combinator value.
  const watchedCombinator = useWatch({
    control: methods.control,
    name: combinatorName,
  }) as { id?: string } | undefined;
  const connectorType = watchedCombinator?.id === "OR" ? "or" : "and";

  return (
    <div
      className={`condition-builder__group ${
        depth % 2 === 0
          ? "condition-builder__group--even"
          : "condition-builder__group--odd"
      }`}
    >
      <div
        className={`condition-builder__body${
          showCombinator
            ? ` condition-builder__body--with-combinator condition-builder__body--${connectorType}`
            : ""
        }`}
      >
        {showCombinator && (
          <div className="condition-builder__combinator">
            <FormDropDownList
              name={combinatorName}
              data={[...COMBINATOR_OPTIONS]}
              textField="name"
              dataItemKey="id"
              disabled={config.readOnly}
              filterable={false}
              disableAutoClear
            />
          </div>
        )}

        <div className="condition-builder__rows">
          {conditions.map((entry, index) => {
            const stableKey =
              (entry as { id?: string })?.id ??
              (fields[index] as { _rhfKey?: string })?._rhfKey ??
              `idx-${index}`;
            if (isConditionGroup(entry)) {
              return (
                <div
                  key={stableKey}
                  className="condition-builder__row-connector condition-builder__row-connector--nested condition-builder__nested-group"
                >
                  <ConditionGroup
                    name={`${name}.${index}.conditions`}
                    combinatorName={`${name}.${index}.combinator`}
                    depth={depth + 1}
                    onRemoveSelf={() => remove(index)}
                  />
                </div>
              );
            }
            return (
              <div key={stableKey} className="condition-builder__row-connector">
                <ConditionRow
                  arrayName={name}
                  index={index}
                  onRemove={(i: number) => remove(i)}
                  onInsert={(i: number, value: ConditionLeaf) =>
                    insert(i, value)
                  }
                  onUpdate={(i: number, value: ConditionEntry) =>
                    update(i, value)
                  }
                />
              </div>
            );
          })}
        </div>
      </div>

      {!config.readOnly && (
        <div className="condition-builder__footer">
          <Button
            size="medium"
            fillMode="link"
            type="button"
            className="condition-builder__add-btn"
            disabled={hasIncompleteRow}
            onClick={event => {
              event.preventDefault();
              append(createConditionLeaf());
            }}
          >
            <span className="condition-builder__add-btn-content">
              <PlusSVG width={14} height={14} />
              &nbsp;Condition
            </span>
          </Button>

          {config.allowNestedConditions && (
            <Button
              size="medium"
              fillMode="link"
              type="button"
              className="condition-builder__add-btn"
              disabled={hasIncompleteRow}
              onClick={event => {
                event.preventDefault();
                append(createConditionGroup());
              }}
            >
              <AddGroupIcon width={14} height={14} />
              &nbsp;Nested Condition
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default ConditionGroup;
